'use client';
import { useCategoryTree } from '@/hooks/category/useCategoryTree';
import { signOutHandler } from '@/utils/auth';
import { mergeGuestCart } from '@/utils/MergeGuestCart';
import { useSession } from 'next-auth/react';
import { useEffect } from 'react';
import { toast } from 'sonner';
import { useRouter, useSearchParams } from 'next/navigation';
import { getErrorMessage, handleAddToActiveCart } from '@/utils/global';
import { useLocale } from 'next-intl';
import { deleteCookie, getCookie } from 'cookies-next';
import {
  event_global_variables,
  event_user_auth,
  payment_failure_event,
} from '@/utils/gtm';
import { usePathname } from '@/i18n/navigation';
import useCart from '@/store/cart.store';
import useCheckout from '@/store/checkout.store';
import { addItemToCart, getCart } from '@/services/cart';
import usePreventScrollOnBack from '@/hooks/usePreventScrollOnBack';

// for any client side code would be global
const GlobalConfig = ({ children }) => {
  usePreventScrollOnBack();
  useCategoryTree();
  const { data: session } = useSession();
  const resetCart = useCart((state) => state.resetCart);
  const setCart = useCart((state) => state.setCart);
  const items = useCheckout((state) => state.items);
  const clearCheckout = useCheckout((state) => state.clearCheckout);
  const isHydrated = useCheckout((state) => state.isHydrated);
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams);
  const locale = useLocale();
  const pathname = usePathname();
  const { push } = useRouter();
  const payment_code = getCookie('payment_code');
  const number_of_products = getCookie('number_of_products');
  const amount = getCookie('amount');
  const checkout_order_id = getCookie('checkout_order_id');
  const logout_event = getCookie('user_logout_event');
  const mage = getCookie('mage');

  useEffect(() => {
    if (session?.user && !mage) {
      signOutHandler();
    }
  }, [session?.user, mage]);

  useEffect(() => {
    if (
      (query?.success == 'false' || query?.success == false) &&
      query?.order_id
    ) {
      handleAddToActiveCart(query?.order_id, push, locale);
      setTimeout(() => {
        payment_failure_event(
          amount,
          payment_code,
          number_of_products,
          query?.payment,
          query?.reason
        );
        deleteCookie('payment_code');
        deleteCookie('number_of_products');
        deleteCookie('amount');
      }, 3000);
      const errorMessage = getErrorMessage(
        query?.payment,
        query?.reason,
        locale
      );
      toast.error(errorMessage, { position: 'top-center' }); // Required by Tabby
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    push,
    query?.order_id,
    query?.success,
    locale,
    query?.payment,
    query?.reason,
  ]);

  // merge guest cart to user cart
  useEffect(() => {
    mergeGuestCart(session);
  }, [session]);

  useEffect(() => {
    if (logout_event) {
      event_user_auth(JSON.parse(decodeURIComponent(logout_event)), 'Logout');
      deleteCookie('user_logout_event');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  useEffect(() => {
    const getActiveCart = async () => {
      if (getCookie('orderStatus') == 'placed') {
        if (getCookie('quote')) {
          if (items?.length > 0) {
            await Promise.all(
              items.map(async (item) => {
                await addItemToCart({
                  mage: getCookie('mage'),
                  quote: getCookie('quote'),
                  sku: item.extension_attributes?.product_sku,
                  qty: item.qty,
                });
              })
            ).then(async () => {
              const res = await getCart({
                mage: getCookie('mage'),
                quote: getCookie('quote'),
              });
              setCart(res?.data?.data);
            });
          }
          deleteCookie('orderStatus');
        } else {
          deleteCookie('orderStatus');
          resetCart();
          clearCheckout();
        }
      }
    };
    isHydrated && getActiveCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHydrated]);

  // Check if he return back from payment with out completing order
  useEffect(() => {
    if (pathname === '/checkout' && !!checkout_order_id) {
      handleAddToActiveCart(checkout_order_id, push, locale);
      setTimeout(() => {
        deleteCookie('checkout_order_id');
      }, 1500);
    } else if (!!checkout_order_id) {
      deleteCookie('checkout_order_id');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (getCookie('orderStatus')) {
      deleteCookie('orderStatus');
      deleteCookie('quote');
      resetCart();
      clearCheckout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const pageTypes = {
      '/checkout': 'Checkout',
      '/cart': 'Cart',
      '/user': 'Account',
      '/blog': 'Blog',
      '/brands': 'Brand Page',
      '/': 'HP',
    };
    setTimeout(() => {
      event_global_variables({
        session,
        store_language: locale,
        page_type: pathname.includes('/product')
          ? 'PDP'
          : pathname.includes('-c-')
            ? 'PLP'
            : pageTypes[pathname]
              ? pageTypes[pathname]
              : 'Others',
      });
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, session?.user]);

  return <>{children}</>;
};

export default GlobalConfig;
